/**
 * Relay query key factory
 *
 * Hierarchical key structure for TanStack Query cache management.
 * All keys include tenantId for multi-tenant cache isolation.
 * All tuples use `as const` — no `any`.
 */

export const relayKeys = {
  all: ['relay'] as const,

  tagCandidates: {
    pending: (tenantId: string) =>
      [...relayKeys.all, 'tag-candidates', 'pending', tenantId] as const,
  },

  categoryUsage: {
    detail: (objectTypeId: string, tenantId: string) =>
      [...relayKeys.all, 'category-usage', objectTypeId, tenantId] as const,
  },

  threads: {
    all: (tenantId: string) =>
      [...relayKeys.all, 'threads', 'all', tenantId] as const,
    triage: (tenantId: string) =>
      [...relayKeys.all, 'threads', 'triage', tenantId] as const,
    detail: (id: string, tenantId: string) =>
      [...relayKeys.all, 'thread', id, tenantId] as const,
    messages: (id: string, tenantId: string) =>
      [...relayKeys.all, 'thread', id, 'messages', tenantId] as const,
    teamsMessages: (id: string, tenantId: string) =>
      [...relayKeys.all, 'thread', id, 'teams-messages', tenantId] as const,
    signals: (id: string, tenantId: string) =>
      [...relayKeys.all, 'thread', id, 'signals', tenantId] as const,
    search: (query: string, tenantId: string) =>
      [...relayKeys.all, 'threads', 'search', query, tenantId] as const,
    forObject: (
      objectTypeId: string,
      objectRecordId: string,
      tenantId: string
    ) =>
      [
        ...relayKeys.all,
        'threads',
        'forObject',
        objectTypeId,
        objectRecordId,
        tenantId,
      ] as const,
  },

  notes: {
    byThread: (threadId: string, tenantId: string) =>
      [...relayKeys.all, 'thread', threadId, 'notes', tenantId] as const,
    detail: (noteId: string, tenantId: string) =>
      [...relayKeys.all, 'notes', noteId, tenantId] as const,
  },

  tasks: {
    byThread: (threadId: string, tenantId: string) =>
      [...relayKeys.all, 'thread', threadId, 'tasks', tenantId] as const,
    detail: (taskId: string, tenantId: string) =>
      [...relayKeys.all, 'tasks', taskId, tenantId] as const,
  },

  config: {
    requiredTypes: (tenantId: string) =>
      [...relayKeys.all, 'config', 'requiredTypes', tenantId] as const,
    orgKnowledge: (tenantId: string) =>
      [...relayKeys.all, 'config', 'orgKnowledge', tenantId] as const,
  },

  trace: {
    /** Broad prefix — use for invalidation to bust all trace queries. */
    all: () => [...relayKeys.all, 'trace'] as const,
    byRefs: (refs: string[], view: string, refMode: string, tenantId: string) =>
      [
        ...relayKeys.all,
        'trace',
        'refs',
        ...[...refs].sort(),
        view,
        refMode,
        tenantId,
      ] as const,
    /**
     * Distinct from `byRefs` — the Activity feed's `useInfiniteQuery` caches
     * `InfiniteData<TraceResponse>`, an incompatible shape to `byRefs`'s plain
     * `TraceResponse` (used by `useTrace`/`useHomeActivityDigest`). Keeping
     * separate keys avoids one query's cache entry silently corrupting the
     * other's expected shape.
     */
    byRefsInfinite: (
      refs: string[],
      view: string,
      refMode: string,
      tenantId: string
    ) =>
      [
        ...relayKeys.all,
        'trace',
        'refsInfinite',
        ...[...refs].sort(),
        view,
        refMode,
        tenantId,
      ] as const,
  },

  pins: {
    list: (tenantId: string) =>
      [...relayKeys.all, 'pins', 'list', tenantId] as const,
  },

  attachments: {
    /**
     * Triage suggestions + dedup verdicts for a sorted set of pending
     * attachment ids. Keyed on the sorted id array so callers with different
     * insertion orders share the same cache entry.
     */
    triageSuggestions: (sortedIds: string[]) =>
      [
        ...relayKeys.all,
        'attachments',
        'triage-suggestions',
        sortedIds,
      ] as const,
    /** Prefix shared by every byThread() key — use this for invalidation. */
    byThreadPrefix: (threadId: string) =>
      [...relayKeys.all, 'thread', threadId, 'attachments'] as const,
    /** Cross-thread pending attachments for the triage feed. */
    pendingForTenant: (tenantId: string) =>
      [
        ...relayKeys.all,
        'attachments',
        'pending-for-tenant',
        tenantId,
      ] as const,
    /** Pending attachments scoped to a set of object-record refs (Library
     *  Files tab). Keyed on the sorted ref set so scope order doesn't fork the
     *  cache. */
    pendingForRefs: (tenantId: string, sortedRefs: string[]) =>
      [
        ...relayKeys.all,
        'attachments',
        'pending-for-refs',
        tenantId,
        sortedRefs,
      ] as const,
    /** Prefix shared by every pendingForRefs() key (any ref set) — use this to
     *  invalidate/optimistically update the Library Files tab's pending queries
     *  from a mutation that only knows the thread, not the scope's ref set. */
    pendingForRefsPrefix: (tenantId: string) =>
      [...relayKeys.all, 'attachments', 'pending-for-refs', tenantId] as const,
    /** Cross-thread ingested (promoted-to-File) attachments — drives the
     *  collapsed-card "has files" indicator. */
    ingestedFilesForTenant: (tenantId: string) =>
      [
        ...relayKeys.all,
        'attachments',
        'ingested-files-for-tenant',
        tenantId,
      ] as const,
    byThread: (threadId: string, tenantId: string, statuses?: string[]) =>
      [
        ...relayKeys.attachments.byThreadPrefix(threadId),
        tenantId,
        ...(statuses ? [...statuses].sort() : []),
      ] as const,
    /** Prefix shared by every teamsByThread() key — use this for invalidation. */
    teamsByThreadPrefix: (threadId: string) =>
      [...relayKeys.all, 'thread', threadId, 'teams-attachments'] as const,
    /** Teams attachments on a thread (separate source table from email). */
    teamsByThread: (threadId: string, tenantId: string, statuses?: string[]) =>
      [
        ...relayKeys.attachments.teamsByThreadPrefix(threadId),
        tenantId,
        ...(statuses ? [...statuses].sort() : []),
      ] as const,
    detail: (attachmentId: string, tenantId: string) =>
      [...relayKeys.all, 'attachments', attachmentId, tenantId] as const,
    /** Reverse lookup: source email for a Document/Record id. */
    sourceByResource: (resourceId: string, tenantId: string) =>
      [
        ...relayKeys.all,
        'attachments',
        'by-resource',
        resourceId,
        tenantId,
      ] as const,
  },
};
